// Progressive, budget-capped pagination for one map layer.
//
// Page 1 is fetched alone to learn totalCount; the remaining pages (up to the
// zoom budget) then go out in parallel and are committed as they land, so pins
// appear in waves instead of after the whole set finishes. Everything runs
// under one AbortController keyed to the query, so panning or changing filters
// cancels the in-flight fan-out rather than letting stale pages race in.
//
// Offset paging rather than cursor paging: cursors are inherently sequential
// (page N+1 needs page N's endCursor), which would serialize a viewport into
// dozens of round-trips. Map data is read-mostly, so the risk of a concurrent
// write shifting rows between offset reads is acceptable against the latency
// win. Ordering is ID_ASC so the sequence is at least stable.
import { useEffect, useRef, useState } from "react";
import { gqlRequest } from "../lib/gql";
import type { Connection } from "../lib/queries";
import { PAGE_SIZE } from "./zoomConfig";

export type LayerState<T> = {
  /** Rows fetched so far — grows as pages land. */
  nodes: T[];
  /** Total matching the filter server-side, before the budget is applied. */
  totalCount: number;
  /** True while any page for the current key is in flight. */
  isFetching: boolean;
  /** True when the budget cut the result short of totalCount. */
  truncated: boolean;
  error: unknown;
};

const EMPTY: LayerState<never> = {
  nodes: [],
  totalCount: 0,
  isFetching: false,
  truncated: false,
  error: null,
};

/** Max pages in flight at once; keeps a wide viewport from opening 50 sockets. */
const MAX_CONCURRENCY = 6;

/** Run tasks with a bounded number in flight, invoking onResult as each lands. */
async function pooled<T>(
  tasks: (() => Promise<T>)[],
  limit: number,
  onResult: (value: T) => void,
): Promise<void> {
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    while (next < tasks.length) {
      const index = next++;
      onResult(await tasks[index]!());
    }
  });
  await Promise.all(workers);
}

export function usePaginatedLayer<T>(opts: {
  /** Stable identity for the request; a change cancels in-flight work. */
  key: string;
  enabled: boolean;
  query: string;
  variables: Record<string, unknown>;
  /** Pulls the connection out of the query result. */
  select: (data: never) => Connection<T>;
  /** Zoom-dependent ceiling on rows for this layer. */
  budget: number;
  /** Rows per request; defaults to PAGE_SIZE. Heavy layers pass less. */
  pageSize?: number;
}): LayerState<T> {
  const { key, enabled, query, variables, select, budget, pageSize = PAGE_SIZE } = opts;
  const [state, setState] = useState<LayerState<T>>(EMPTY as LayerState<T>);

  // Held in refs so the effect depends only on `key`: re-running on every
  // render-new `variables` object would cancel and refetch endlessly.
  const latest = useRef({ query, variables, select, budget, enabled, pageSize });
  latest.current = { query, variables, select, budget, enabled, pageSize };

  useEffect(() => {
    const { query, variables, select, budget, enabled, pageSize } = latest.current;
    if (!enabled) {
      // Clear rather than leaving the last result visible: a toggled-off layer
      // must not keep painting its pins.
      setState(EMPTY as LayerState<T>);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    // keepPreviousData equivalent: hold the old nodes on screen while the new
    // viewport loads, but mark it fetching so the spinner shows.
    setState((prev) => ({ ...prev, isFetching: true, error: null }));

    (async () => {
      try {
        // Never ask for more than the budget allows: at low zoom the budget is
        // well under PAGE_SIZE, and fetching a full page there would pay for
        // rows that are immediately discarded.
        const firstSize = Math.min(pageSize, budget);
        // Only the first page asks for totalCount — the count(*) subquery
        // re-runs the whole bbox filter server-side, so every later page
        // would pay it again for a number we already have.
        const firstRaw = await gqlRequest<never>(
          query,
          { ...variables, first: firstSize, offset: 0, withTotal: true },
          signal,
        );
        if (signal.aborted) return;

        const first = select(firstRaw);
        const total = first.totalCount ?? first.nodes.length;
        const target = Math.min(total, budget);

        // First page replaces the previous viewport's rows; later pages append.
        setState({
          nodes: first.nodes.slice(0, target),
          totalCount: total,
          isFetching: first.nodes.length < target,
          truncated: total > target,
          error: null,
        });

        if (first.nodes.length >= target || first.nodes.length === 0) return;

        // Stride from what the server actually returned rather than from
        // PAGE_SIZE. pdbq now rejects an oversized `first:` outright, so a
        // silent clamp is no longer possible — but deriving the stride keeps
        // the offsets correct for free if the server ever returns a short page
        // for some other reason.
        const stride = first.nodes.length;

        const offsets: number[] = [];
        for (let offset = stride; offset < target; offset += stride) offsets.push(offset);

        const tasks = offsets.map((offset) => async () => {
          const raw = await gqlRequest<never>(
            query,
            { ...variables, first: Math.min(stride, target - offset), offset, withTotal: false },
            signal,
          );
          return select(raw).nodes;
        });

        await pooled(tasks, MAX_CONCURRENCY, (nodes) => {
          if (signal.aborted) return;
          // Commit each page as it arrives so the map fills in progressively.
          setState((prev) => ({ ...prev, nodes: [...prev.nodes, ...nodes] }));
        });

        if (!signal.aborted) setState((prev) => ({ ...prev, isFetching: false }));
      } catch (err) {
        // An abort is the expected outcome of panning, not a failure.
        if (signal.aborted || (err instanceof DOMException && err.name === "AbortError")) return;
        setState((prev) => ({ ...prev, isFetching: false, error: err }));
      }
    })();

    return () => controller.abort();
  }, [key]);

  return state;
}
